import React from 'react';
import { ChevronDown, Grid3X3 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { SidebarFilterButton } from '@/components/ui/SidebarFilterButtons';
import CountSkeleton from '@/components/ui/CountSkeleton';

interface ContentTypeDropdownProps {
  selectedContentType: string;
  onContentTypeChange: (type: string) => void;
  availableContentTypes: Array<{
    type: string;
    name: string;
    icon: React.ComponentType<any>;
    variant: 'green' | 'purple' | 'blue' | 'orange' | 'red' | 'indigo' | 'gray' | 'amber';
    iconColor: string;
    count?: number;
  }>;
  totalCount?: number;
  isLoadingCounts?: boolean;
}

const ContentTypeDropdown: React.FC<ContentTypeDropdownProps> = ({
  selectedContentType,
  onContentTypeChange,
  availableContentTypes,
  totalCount,
  isLoadingCounts = false,
}) => {
  // Get selected content type info for trigger display
  const selectedTypeInfo = selectedContentType === 'all' 
    ? null 
    : availableContentTypes.find(type => type.type === selectedContentType);

  // Display text for trigger
  const triggerText = selectedTypeInfo ? selectedTypeInfo.name : 'All Content Types';
  const triggerIcon = selectedTypeInfo ? selectedTypeInfo.icon : Grid3X3;
  const triggerIconColor = selectedTypeInfo ? selectedTypeInfo.iconColor : 'text-orange-500';
  const triggerCount = selectedContentType === 'all' 
    ? totalCount 
    : selectedTypeInfo?.count || 0;

  const TriggerIcon = triggerIcon;
  
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className={cn(
            "w-full flex items-center justify-between px-4 py-2 rounded-md text-sm font-medium",
            "bg-white/30 dark:bg-white/10 backdrop-blur-sm",
            "border border-accent/20 focus:border-accent/40",
            "text-slate-700 dark:text-slate-200",
            "shadow-sm hover:shadow-md transition-all duration-300",
            "gap-3"
          )}
        >
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <TriggerIcon 
              size={16} 
              className={triggerIconColor}
            />
            <span className="truncate">{triggerText}</span>
            {!isLoadingCounts && (
              <span className={cn(
                'ml-auto px-2 py-0.5 text-xs font-medium rounded-full',
                selectedContentType === 'all'
                  ? 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300'
                  : selectedTypeInfo?.variant === 'green'
                    ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
                    : selectedTypeInfo?.variant === 'purple'
                      ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300'
                      : selectedTypeInfo?.variant === 'blue'
                        ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
                        : 'bg-gray-100 dark:bg-gray-900/40 text-gray-700 dark:text-gray-300'
              )}>
                {triggerCount}
              </span>
            )}
          </div>
          <ChevronDown size={16} className="text-accent flex-shrink-0" />
        </motion.button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent 
        align="start" 
        className="w-[var(--radix-dropdown-menu-trigger-width)] bg-white/90 dark:bg-black/90 backdrop-blur-md border border-accent/20"
        sideOffset={4}
      >
        <div className="p-1 space-y-1">
          {/* All Content Types option */}
          <SidebarFilterButton
            variant="orange"
            isActive={selectedContentType === 'all'}
            count={isLoadingCounts ? undefined : totalCount}
            countElement={isLoadingCounts ? <CountSkeleton /> : undefined}
            onClick={() => onContentTypeChange('all')}
            className="w-full"
          >
            <Grid3X3 className="text-orange-500" />
            <span>All Content Types</span>
          </SidebarFilterButton>
          
          {/* Content type options */}
          {availableContentTypes.map((type) => (
            <SidebarFilterButton
              key={type.type}
              variant={type.variant}
              isActive={selectedContentType === type.type}
              count={isLoadingCounts ? undefined : type.count}
              countElement={isLoadingCounts ? <CountSkeleton /> : undefined}
              onClick={() => onContentTypeChange(type.type)}
              className="w-full"
            >
              <type.icon className={type.iconColor} />
              <span>{type.name}</span>
            </SidebarFilterButton>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ContentTypeDropdown; 